import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "@banno/jha-wc/src/buttons/jha-button/jha-button";
import "@banno/jha-wc/src/forms/jha-form-input-base/jha-form-input";
import "@banno/jha-wc/src/jha-tooltip/jha-tooltip";
import "@banno/jha-wc/src/icons/jha-icon-plus";
import "@jack-henry/jh-ui/components/input/input";
import "@jack-henry/jh-icons/icons-wc/icon-arrow-down-to-bracket";
/**
 * Interface for variable options passed to the component
 */
export interface TemplateStringInputOption {
  name: string;
  value: string;
  description?: string;
}

/**
 * Interface for new variable data when adding a variable.
 * Uses camelCase to match WorkflowTemplateVariable interface and API conventions.
 */
export interface NewVariableData {
  name: string;
  description: string;
  regexRule: string;
  ruleDescription: string;
}

/**
 * TemplateStringInput Component
 *
 * A text input component that supports inserting template variables in the format {variableName}.
 *
 * Features:
 * - Click icon or type "{" to open variable selection dropdown
 * - First dropdown item is always "Add Variable" option
 * - Supports multiple variables in a single input (e.g., "{prefix}_file_{suffix}.txt")
 * - Variables are protected - deleting any part of a variable selects the whole variable
 * - Escape or blur restores original value when dropdown was opened
 * - Inline form for adding new variables with Name, Description, Rules, Rule Description
 *
 * Events:
 * - `change`: Fired when the value changes. Detail: { value: string }
 * - `variable-added`: Fired when a new variable is added via the inline form.
 *                     Detail: NewVariableData
 */
@customElement("template-string-input")
export class TemplateStringInput extends LitElement {
  // ============================================
  // Properties
  // ============================================

  /** Current value of the input */
  @property({ type: String })
  value = "";

  /** Placeholder text for the input */
  @property({ type: String })
  placeholder = "";

  /** Label for the input */
  @property({ type: String })
  label = "";

  /** Whether the input is disabled */
  @property({ type: Boolean })
  disabled = false;

  /** Whether the input is required */
  @property({ type: Boolean })
  required = false;

  /** Max character length of the input. 0 means unlimited. */
  @property({ type: Number })
  maxlength = 0;

  /** Custom error text to display instead of default "[label] is required" */
  @property({ type: String })
  errorText = "";

  /** Force show warning even if field is not touched or not empty */
  @property({ type: Boolean })
  forceShowWarning = false;

  /** 
   * Position of the variable selection dropdown relative to the input.
   * Controls where the list of variables appears when clicking the variable icon or typing '{'.
   * Values: "bottom", "bottom-right", "bottom-left", "top", "top-right", "top-left", "left", "right"
   * Default: "bottom"
   */
  @property({ type: String })
  variableDropdownPosition: "bottom" | "bottom-right" | "bottom-left" | "top" | "top-right" | "top-left" | "left" | "right" = "bottom";

  /** 
   * Position of the tooltip on the variable button.
   * Controls where the "Click to insert a variable" hint appears when hovering over the variable icon.
   * Values: "bottom", "bottom-right", "bottom-left", "top", "top-right", "top-left", "left", "right"
   * Default: "bottom"
   */
  @property({ type: String })
  variableButtonTooltipPosition: "bottom" | "bottom-right" | "bottom-left" | "top" | "top-right" | "top-left" | "left" | "right" = "bottom";

  /** Array of variable options to display in dropdown */
  @property({ type: Array })
  options: TemplateStringInputOption[] = [];

  // ============================================
  // State
  // ============================================

  /** Whether the variable dropdown is open */
  @state()
  private isDropdownOpen = false;

  /** Whether the Add Variable form is showing */
  @state()
  private showAddVariableForm = false;

  /** Original value when dropdown was opened (for cancel/escape) */
  @state()
  private originalValue = "";

  /** Cursor position when dropdown was triggered */
  @state()
  private insertPosition = 0;

  /** Range of variable being edited [start, end] including { } delimiters */
  @state()
  private editingVariableRange: [number, number] | null = null;

  /** Previous value to detect partial variable deletion */
  @state()
  private previousValue = "";

  /** Last known cursor position (saved before focus is lost) */
  private lastCursorPosition = 0;

  /** Whether the field has been touched (blurred at least once) */
  @state()
  private touched = false;

  // Add Variable Form State
  @state() private newVarName = "";
  @state() private newVarDescription = "";
  @state() private newVarRegex = "";
  @state() private newVarRegexDescription = "";
  @state() private formErrorMessage = "";

  // Valid characters for variable names (a-zA-Z0-9_-)
  private static readonly VALID_VAR_NAME_CHARS = /^[a-zA-Z0-9_\-]$/;
  // Characters that cannot be at the start of a variable name
  private static readonly INVALID_START_CHARS = /^[-_]$/;

  // Dropdown position for fixed positioning (to escape overflow:hidden containers)
  @state() private dropdownPosition = { top: 0, left: 0, width: 0, maxHeight: 200, flipAbove: false };

  // Tooltip state for variable hover hints
  @state() private activeTooltip: { text: string; itemIndex: number } | null = null;
  @state() private tooltipPosition = { top: 0, left: 0, preferredSide: "right" as "left" | "right" | "top" | "bottom" };

  // Bound event handler references for proper cleanup
  private boundHandleGlobalKeydown!: (e: KeyboardEvent) => void;
  private boundHandleGlobalClick!: (e: MouseEvent) => void;
  private boundHandleGlobalFocusIn!: (e: FocusEvent) => void;

  // ============================================
  // Styles
  // ============================================

  static styles = css`
    :host {
      display: block;
      position: relative;
    }

    /* When modal is open, elevate the entire component above everything */
    :host(.modal-active) {
      z-index: 100000;
      position: relative;
    }

    .input-container {
      position: relative;
    }

    /* Custom label matching jha-form-select label styling */
    .field-label {
      display: block;
      font-size: 12px;
      font-weight: 400;
      color: inherit;
      margin-bottom: 6px;
    }

    .field-label .required-asterisk {
      color: var(--jha-color-danger, #c9252d);
      margin-left: 2px;
    }

    .input-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    /* Hide the built-in jh-input label since we use our custom one */
    jh-input::part(label) {
      display: none;
    }

    jh-input {
      flex: 1;
      /* Match jha-form-input font styling - uses system-ui font stack */
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      font-size: 15px;
      font-weight: 400;
      /* Match jha-form-select background color */
      --jh-color-container-primary-enabled: transparent;
      /* Set placeholder color CSS variables to match jha-form-input */
      --jh-color-content-tertiary-enabled: #999999;
      --jh-input-placeholder-color: #999999;
      --jh-color-content-secondary-enabled: #999999;
    }

    /* Target the input part directly for background and font */
    jh-input::part(input) {
      background: transparent;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      font-size: 15px;
      font-weight: 400;
    }

    @media (prefers-color-scheme: dark) {
      jh-input {
        --jh-color-content-tertiary-enabled: #808080;
        --jh-input-placeholder-color: #808080;
        --jh-color-content-secondary-enabled: #808080;
      }
    }

    /* Validation error styling - matching jh-input error text */
    .validation-message {
      display: block;
      font-size: 12px;
      color: var(--jh-color-content-negative-enabled, #c9252d);
      margin-top: 4px;
    }

    /* Error state border - set CSS variables for jh-input */
    .input-wrapper.has-error jh-input {
      --jh-color-border-primary-enabled: var(--jh-color-content-negative-enabled, #c9252d);
    }

    .variable-button {
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: background-color 0.2s;
      height: 40px;
      width: 40px;
      box-sizing: border-box;
      margin-top: -4px;
    }

    .variable-button:hover {
      background-color: var(--jha-color-background-hover, rgba(0, 0, 0, 0.05));
    }

    .variable-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    jh-icon-arrow-down-to-bracket {
      width: 24px;
      height: 24px;
      color: var(--jha-color-primary);
    }

    /* Dropdown container - uses absolute positioning relative to input-wrapper */
    .dropdown-container {
      position: absolute;
      z-index: 100000;
      min-width: 220px;
      max-width: 320px;
      background-color: #ffffff;
      border: 1px solid #cccccc;
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    @media (prefers-color-scheme: dark) {
      .dropdown-container {
        background-color: #1e1e1e;
        border-color: #444444;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
      }
    }

    /* Modal overlay backdrop - for Add Variable form */
    /* Use very high z-index to ensure modal appears above all other UI elements */
    /* including progress bars (z-index: 1500), tooltips, and dialog chrome */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.5);
      z-index: 100000;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    /* Modal container - centered dialog */
    .modal-container {
      position: relative;
      z-index: 100001;
      min-width: 500px;
      max-width: 600px;
      background-color: #ffffff;
      border: 1px solid #cccccc;
      border-radius: 8px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
    }

    @media (prefers-color-scheme: dark) {
      .modal-container {
        background-color: #1e1e1e;
        border-color: #444444;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
      }

      .modal-container .form-hint {
        color: #aaaaaa;
      }

      .modal-container .form-close {
        color: #aaaaaa;
      }

      .modal-container .form-close:hover {
        color: #ffffff;
        background-color: #333333;
      }
    }

    /* Dropdown menu items - compact with scroll */
    .dropdown-menu {
      max-height: var(--dropdown-max-height, 200px);
      overflow-y: auto;
      padding: 4px 0;
      scrollbar-width: thin;
    }

    /* Scrollbar styling for webkit browsers */
    .dropdown-menu::-webkit-scrollbar {
      width: 8px;
    }

    .dropdown-menu::-webkit-scrollbar-track {
      background: transparent;
    }

    .dropdown-menu::-webkit-scrollbar-thumb {
      background-color: rgba(128, 128, 128, 0.5);
      border-radius: 4px;
    }

    .dropdown-menu::-webkit-scrollbar-thumb:hover {
      background-color: rgba(128, 128, 128, 0.7);
    }

    .menu-item {
      padding: 6px 12px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 6px;
      transition: background-color 0.15s;
      font-size: 13px;
    }

    .menu-item:hover {
      background-color: rgba(0, 0, 0, 0.08);
    }

    @media (prefers-color-scheme: dark) {
      .menu-item:hover {
        background-color: rgba(255, 255, 255, 0.1);
      }
    }

    .menu-item:focus {
      outline: 2px solid var(--jha-color-primary);
      outline-offset: -2px;
    }

    /* Add Variable option styling */
    .add-variable-item {
      color: var(--jha-color-primary);
      font-weight: 600;
      border-bottom: 1px solid #eeeeee;
      margin-bottom: 2px;
      padding-bottom: 8px;
    }

    @media (prefers-color-scheme: dark) {
      .add-variable-item {
        border-bottom-color: #444444;
      }
    }

    .add-variable-item jha-icon-plus {
      fill: var(--jha-color-primary);
      width: 16px;
      height: 16px;
    }

    /* Variable item styling */
    .variable-item {
      font-family: monospace;
      font-size: 13px;
    }

    .variable-delimiters {
      color: var(--jha-color-primary);
      font-weight: 600;
    }

    /* No variables message */
    .no-variables {
      padding: 10px 12px;
      text-align: center;
      color: #888888;
      font-style: italic;
      font-size: 12px;
    }

    @media (prefers-color-scheme: dark) {
      .no-variables {
        color: #aaaaaa;
      }
    }

    /* Add Variable Form - 2 column layout */
    .add-variable-form {
      padding: 20px;
      min-width: 480px;
      background-color: inherit;
    }

    .form-title {
      font-weight: 600;
      font-size: 16px;
      margin-bottom: 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .form-error-message-bottom {
      color: #c00;
      font-size: 12px;
      margin-top: 8px;
      margin-bottom: 12px;
      line-height: 1.4;
    }

    .form-close {
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 8px 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      font-size: 28px;
      line-height: 1;
      color: var(--jha-text-color-secondary, #666);
      min-width: 40px;
      min-height: 40px;
    }

    .form-close:hover {
      background-color: var(--jha-color-background-hover, rgba(0, 0, 0, 0.05));
      color: var(--jha-text-color, #333);
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px 16px;
    }

    .form-field {
      display: flex;
      flex-direction: column;
    }

    .form-field jha-form-input {
      width: 100%;
    }

    .form-field.optional label::after {
      content: " (optional)";
      font-weight: 400;
      font-style: italic;
    }

    .form-actions {
      display: flex;
      gap: 8px;
      justify-content: flex-end;
      margin-top: 16px;
      grid-column: 1 / -1;
    }

    .form-hint {
      font-size: 11px;
      color: var(--jha-text-color-secondary, #888);
      margin-top: 4px;
    }

    /* Required asterisk styling */
    .required-label::after {
      content: " *";
      color: var(--jha-color-danger, #dc3545);
    }

    /* Variable tooltip - positioned relative to menu item */
    .variable-tooltip {
      position: fixed;
      z-index: 100001;
      background-color: #2d2d2d;
      color: #ffffff;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 12px;
      line-height: 1.4;
      max-width: 280px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      pointer-events: none;
      word-wrap: break-word;
      white-space: normal;
    }

    @media (prefers-color-scheme: dark) {
      .variable-tooltip {
        background-color: #3a3a3a;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
      }
    }

    /* Arrow for tooltip - positioned based on side */
    .variable-tooltip::before {
      content: "";
      position: absolute;
      width: 0;
      height: 0;
      border-style: solid;
    }

    /* Arrow pointing left (tooltip on right) */
    .variable-tooltip.tooltip-right::before {
      left: -6px;
      top: 50%;
      transform: translateY(-50%);
      border-width: 6px 6px 6px 0;
      border-color: transparent #2d2d2d transparent transparent;
    }

    @media (prefers-color-scheme: dark) {
      .variable-tooltip.tooltip-right::before {
        border-color: transparent #3a3a3a transparent transparent;
      }
    }

    /* Arrow pointing right (tooltip on left) */
    .variable-tooltip.tooltip-left::before {
      right: -6px;
      top: 50%;
      transform: translateY(-50%);
      border-width: 6px 0 6px 6px;
      border-color: transparent transparent transparent #2d2d2d;
    }

    @media (prefers-color-scheme: dark) {
      .variable-tooltip.tooltip-left::before {
        border-color: transparent transparent transparent #3a3a3a;
      }
    }

    /* Arrow pointing up (tooltip on bottom) */
    .variable-tooltip.tooltip-bottom::before {
      top: -6px;
      left: 50%;
      transform: translateX(-50%);
      border-width: 0 6px 6px 6px;
      border-color: transparent transparent #2d2d2d transparent;
    }

    @media (prefers-color-scheme: dark) {
      .variable-tooltip.tooltip-bottom::before {
        border-color: transparent transparent #3a3a3a transparent;
      }
    }

    /* Arrow pointing down (tooltip on top) */
    .variable-tooltip.tooltip-top::before {
      bottom: -6px;
      left: 50%;
      transform: translateX(-50%);
      border-width: 6px 6px 0 6px;
      border-color: #2d2d2d transparent transparent transparent;
    }

    @media (prefers-color-scheme: dark) {
      .variable-tooltip.tooltip-top::before {
        border-color: #3a3a3a transparent transparent transparent;
      }
    }
  `;

  // ============================================
  // Lifecycle
  // ============================================

  connectedCallback() {
    super.connectedCallback();
    this.previousValue = this.value || "";

    // Bind event handlers to maintain proper 'this' context
    this.boundHandleGlobalKeydown = this.handleGlobalKeydown.bind(this);
    this.boundHandleGlobalClick = this.handleGlobalClick.bind(this);
    this.boundHandleGlobalFocusIn = this.handleGlobalFocusIn.bind(this);

    // Add global escape key listener
    document.addEventListener("keydown", this.boundHandleGlobalKeydown);
    
    // Add global click listener to close dropdown when clicking outside
    document.addEventListener("click", this.boundHandleGlobalClick);
    
    // Add global focusin listener to close dropdown when focusing another element
    document.addEventListener("focusin", this.boundHandleGlobalFocusIn);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("keydown", this.boundHandleGlobalKeydown);
    document.removeEventListener("click", this.boundHandleGlobalClick);
    document.removeEventListener("focusin", this.boundHandleGlobalFocusIn);
  }

  /**
   * Called after the first render. Inject placeholder styles into jh-input shadow DOM.
   * This ensures placeholder styling matches jha-form-input.
   */
  firstUpdated() {
    this.applyNativeMaxLength();
    // Inject placeholder styles into jh-input shadow DOM
    // Use a small delay to ensure jh-input has fully rendered
    setTimeout(() => {
      this.injectPlaceholderStyles();
      this.applyNativeMaxLength();
    }, 50);
  }

  updated() {
    this.applyNativeMaxLength();
  }

  /**
   * jh-input maxlength is a String property forwarded with ifDefined. Setting the
   * attribute on the host is not enough — paste still writes the inner native input
   * unless maxLength is on that node. Same gap as jha-form-input (constructor null/Infinity).
   */
  private applyNativeMaxLength() {
    const max = this.maxlength > 0 ? this.maxlength : 0;
    const jhInput = this.shadowRoot?.querySelector("jh-input") as
      | (HTMLElement & { maxlength?: string | null })
      | null;
    if (jhInput) {
      jhInput.maxlength = max > 0 ? String(max) : null;
    }
    const native = this.inputEl;
    if (!native) {
      return;
    }
    if (max > 0) {
      native.maxLength = max;
      native.setAttribute("maxlength", String(max));
    } else {
      native.removeAttribute("maxlength");
    }
  }

  /**
   * Inject placeholder styles into the jh-input shadow DOM to match jha-form-input styling.
   * The jh-input component uses shadow DOM, so we need to inject styles directly.
   */
  private injectPlaceholderStyles() {
    const jhInput = this.shadowRoot?.querySelector("jh-input");
    if (jhInput?.shadowRoot) {
      // Check if styles are already injected
      if (jhInput.shadowRoot.querySelector("#placeholder-styles")) {
        return;
      }

      const styleEl = document.createElement("style");
      styleEl.id = "placeholder-styles";
      // Match jha-form-input placeholder styling exactly
      // Using system-ui font stack to match JHA design system (--jha-platform-font)
      styleEl.textContent = `
        input {
          font-family: system-ui, -apple-system, BlinkMacSystemFont, sans-serif !important;
          font-size: 15px !important;
          font-weight: 400 !important;
          line-height: 24px !important;
        }
        
        input::placeholder {
          color: #999999 !important;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, sans-serif !important;
          font-size: 15px !important;
          font-weight: 400 !important;
          font-style: normal !important;
          opacity: 1 !important;
        }
        
        @media (prefers-color-scheme: dark) {
          input::placeholder {
            color: #808080 !important;
          }
        }
      `;
      jhInput.shadowRoot.appendChild(styleEl);
    } else {
      // Retry if jh-input shadow root not ready yet
      requestAnimationFrame(() => this.injectPlaceholderStyles());
    }
  }

  // ============================================
  // Getters
  // ============================================

  /** Get the native input element from jh-input */
  private get inputEl(): HTMLInputElement | null {
    const jhInput = this.shadowRoot?.querySelector("jh-input");
    return jhInput?.shadowRoot?.querySelector("input") as HTMLInputElement | null;
  }

  /** Find all template variable ranges in the current value ({variableName}) */
  private get variableRanges(): Array<{ start: number; end: number; name: string }> {
    const ranges: Array<{ start: number; end: number; name: string }> = [];
    const val = this.value || "";
    const regex = /\{([a-zA-Z0-9][a-zA-Z0-9_\-]*)\}/g;
    let match;

    while ((match = regex.exec(val)) !== null) {
      ranges.push({
        start: match.index,
        end: match.index + match[0].length,
        name: match[1],
      });
    }

    return ranges;
  }

  /** Check if cursor is inside a variable */
  private getVariableAtPosition(position: number): { start: number; end: number; name: string } | null {
    for (const range of this.variableRanges) {
      if (position > range.start && position < range.end) {
        return range;
      }
    }
    return null;
  }

  // ============================================
  // Event Handlers
  // ============================================

  /** Handle global keydown for escape */
  private handleGlobalKeydown(e: KeyboardEvent) {
    if (e.key === "Escape" && this.isDropdownOpen) {
      // MODAL BEHAVIOR: Don't close Add Variable form with ESC
      // Only close the variable dropdown list with ESC
      if (!this.showAddVariableForm) {
        e.preventDefault();
        this.cancelAndClose();
      }
      // If showAddVariableForm is true, do nothing - force users to use Cancel/X button
    }
  }

  /** Handle global click to close dropdown when clicking outside this component */
  private handleGlobalClick(e: MouseEvent) {
    if (!this.isDropdownOpen) return;
    
    // MODAL BEHAVIOR: Don't close on outside click when Add Variable form is showing
    // Users must explicitly click Cancel or X button
    if (this.showAddVariableForm) return;
    
    // Check if click is inside this component
    const path = e.composedPath();
    const isInsideComponent = path.includes(this);
    
    if (!isInsideComponent) {
      this.closeDropdown();
    }
  }

  /** Handle global focusin to close dropdown when focusing another element outside this component */
  private handleGlobalFocusIn(e: FocusEvent) {
    if (!this.isDropdownOpen) return;
    
    // MODAL BEHAVIOR: Don't close on outside focus when Add Variable form is showing
    // Users must explicitly click Cancel or X button
    if (this.showAddVariableForm) return;
    
    // Check if the newly focused element is inside this component
    const path = e.composedPath();
    const isInsideComponent = path.includes(this);
    
    if (!isInsideComponent) {
      this.closeDropdown();
    }
  }

  /** Save the current cursor position (called on keyup, click, select) */
  private saveCursorPosition() {
    const nativeInput = this.inputEl;
    if (nativeInput) {
      this.lastCursorPosition = nativeInput.selectionStart ?? (this.value || "").length;
    }
  }

  /** Handle input value changes from jh-input */
  private handleInput(e: Event) {
    // CRITICAL: Only process input events from the MAIN input, not from popup form fields
    // Events from popup's jha-form-input elements can bubble up and trigger this handler
    const target = e.target as any;
    const nativeInput = this.inputEl;
    const jhInputElement = this.shadowRoot?.querySelector("jh-input");
    
    // Accept events from EITHER the jh-input wrapper OR the native input element
    // (Events bubble from the native input through jh-input)
    if (target !== nativeInput && target !== jhInputElement) {
      return;
    }
    
    let newValue = target?.value ?? "";
    if (this.maxlength > 0 && newValue.length > this.maxlength) {
      newValue = newValue.slice(0, this.maxlength);
      if (nativeInput) {
        nativeInput.value = newValue;
      }
      if (jhInputElement) {
        (jhInputElement as HTMLInputElement).value = newValue;
      }
    }
    const cursorPos = nativeInput?.selectionStart || 0;

    // Save cursor position
    this.lastCursorPosition = cursorPos;

    // Check if a variable was partially deleted
    if (this.detectPartialVariableDeletion(this.previousValue, newValue)) {
      const affectedVariable = this.findAffectedVariable(this.previousValue, newValue, cursorPos);

      if (affectedVariable) {
        // Remove the entire variable and open dropdown
        const beforeVar = this.previousValue.slice(0, affectedVariable.start);
        const afterVar = this.previousValue.slice(affectedVariable.end);
        this.value = beforeVar + afterVar;
        this.previousValue = this.value;
        this.insertPosition = affectedVariable.start;
        this.openDropdown();
        this.dispatchChangeEvent();
        return;
      }
    }

    this.value = newValue;
    this.previousValue = newValue;
    this.dispatchChangeEvent();
  }

  /** Handle keydown in input */
  private handleKeydown(e: KeyboardEvent) {
    if (e.key === "{") {
      e.preventDefault();
      // Use the saved cursor position
      this.insertPosition = this.lastCursorPosition;
      this.openDropdown();
    }
    if (e.key === "Tab" && this.isDropdownOpen) {
      this.closeDropdown();
    }
  }

  /** Handle click on input - check if clicking inside a variable */
  private handleInputClick() {
    const nativeInput = this.inputEl;
    if (!nativeInput) return;

    // Save cursor position
    this.saveCursorPosition();

    const cursorPos = nativeInput.selectionStart || 0;
    const variable = this.getVariableAtPosition(cursorPos);

    if (variable) {
      // Clicked inside a variable - select it and open dropdown
      this.editingVariableRange = [variable.start, variable.end];
      this.insertPosition = variable.start;

      // Select the variable text
      requestAnimationFrame(() => {
        nativeInput.setSelectionRange(variable.start, variable.end);
      });

      this.openDropdown();
    }
  }

  /** Handle blur on input - mark as touched for validation and dispatch event */
  private handleBlur() {
    this.touched = true;
    if (this.isDropdownOpen) {
      this.closeDropdown();
    }
    // Dispatch a custom blur event that crosses Shadow DOM boundaries
    // This allows parent components to validate when the field loses focus
    this.dispatchEvent(
      new CustomEvent("template-input-blur", {
        detail: { value: this.value },
        bubbles: true,
        composed: true,
      })
    );
  }

  /** Public method to trigger validation (for form submission) */
  public validate(): boolean {
    this.touched = true;
    const isValid = !this.required || (this.value || "").trim().length > 0;
    return isValid;
  }

  /** Public method to check if field is valid without marking touched */
  public isValid(): boolean {
    return !this.required || (this.value || "").trim().length > 0;
  }

  /** Handle variable button click */
  private handleVariableButtonClick() {
    if (this.disabled) return;

    if (this.isDropdownOpen) {
      this.cancelAndClose();
    } else {
      // Use the last saved cursor position (saved before input lost focus)
      this.insertPosition = this.lastCursorPosition;
      this.openDropdown();
    }
  }

  /** Handle click on modal overlay (outside the form) */
  private handleOverlayClick() {
    // MODAL BEHAVIOR: Don't close on backdrop click for Add Variable form
    // Users must explicitly click Cancel or X button
    if (!this.showAddVariableForm) {
      // Only close dropdown (variable list), not the Add Variable form
      this.cancelAndClose();
    }
    // If showAddVariableForm is true, do nothing - force users to use Cancel/X button
  }

  /** Handle selecting a variable from dropdown */
  private handleVariableSelect(variableName: string) {
    const variableText = `{${variableName}}`;
    const currentValue = this.value || "";
    let newCursorPos: number;

    if (this.editingVariableRange) {
      // Replace existing variable
      const before = currentValue.slice(0, this.editingVariableRange[0]);
      const after = currentValue.slice(this.editingVariableRange[1]);
      this.value = before + variableText + after;
      newCursorPos = before.length + variableText.length;
    } else {
      // Insert at cursor position
      const insertPos = this.insertPosition ?? currentValue.length;
      const before = currentValue.slice(0, insertPos);
      const after = currentValue.slice(insertPos);
      this.value = before + variableText + after;
      newCursorPos = before.length + variableText.length;
    }

    this.previousValue = this.value;
    this.closeDropdown();
    this.dispatchChangeEvent();

    // Focus back on input and position cursor
    requestAnimationFrame(() => {
      const nativeInput = this.inputEl;
      if (nativeInput) {
        nativeInput.focus();
        nativeInput.setSelectionRange(newCursorPos, newCursorPos);
      }
    });
  }

  /** Handle Add Variable click - switches from dropdown to modal */
  private handleAddVariableClick(e?: Event) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    this.showAddVariableForm = true;
    this.formErrorMessage = ""; // Clear any previous errors
    // Add class to host to elevate z-index above all other elements
    this.classList.add("modal-active");
    // Dispatch event so parent components can adjust their z-index
    this.dispatchEvent(
      new CustomEvent("modal-opened", {
        bubbles: true,
        composed: true,
      })
    );
    this.requestUpdate();
  }

  /** Handle canceling the add variable form */
  private handleAddVariableCancel(e?: Event) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    this.value = this.originalValue;
    this.previousValue = this.originalValue;
    this.closeDropdown(); // Handles cleanup, class removal, and event dispatch
    this.requestUpdate();

    setTimeout(() => {
      this.inputEl?.focus();
    }, 100);
  }

  /** Handle form field change */
  private handleFormFieldChange(field: string, e: Event) {
    // Prevent event from bubbling up to main input handlers
    e.stopPropagation();
    
    const value = (e.target as any).value || "";
    const target = e.target as any;
    
    // Clear form error message when user starts typing
    if (this.formErrorMessage) {
      this.formErrorMessage = "";
    }
    
    // Clear field-level errors when user starts typing
    if (target) {
      target.invalid = false;
      target.warning = "";
    }
    
    switch (field) {
      case "name":
        this.newVarName = value;
        break;
      case "description":
        this.newVarDescription = value;
        break;
      case "regex":
        this.newVarRegex = value;
        break;
      case "regexDesc":
        this.newVarRegexDescription = value;
        break;
    }
  }

  /**
   * Handle keydown in Variable Name field - block invalid characters.
   * Allowed: a-zA-Z0-9_- but cannot start with - or _
   */
  private handleVarNameKeydown(e: KeyboardEvent) {
    // Prevent event from bubbling up to main input handlers
    e.stopPropagation();
    
    // Allow control keys (backspace, delete, arrow keys, tab, etc.)
    if (
      e.key === "Backspace" ||
      e.key === "Delete" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "ArrowUp" ||
      e.key === "ArrowDown" ||
      e.key === "Tab" ||
      e.key === "Home" ||
      e.key === "End" ||
      e.key === "Enter" ||
      e.key === "Escape" ||
      e.ctrlKey ||
      e.metaKey
    ) {
      return; // Allow these keys
    }

    const input = e.target as HTMLInputElement;
    const currentValue = input?.value || "";
    const selectionStart = input?.selectionStart ?? currentValue.length;

    // If this would be the first character, don't allow - or _
    if (selectionStart === 0 && currentValue.length === 0) {
      if (TemplateStringInput.INVALID_START_CHARS.test(e.key)) {
        e.preventDefault();
        return;
      }
    }

    // Block any character that's not in the valid set
    if (!TemplateStringInput.VALID_VAR_NAME_CHARS.test(e.key)) {
      e.preventDefault();
    }
  }

  /**
   * Handle paste in Variable Name field - sanitize pasted content.
   * Removes invalid characters and ensures it doesn't start with - or _
   */
  private handleVarNamePaste(e: ClipboardEvent) {
    e.preventDefault();

    const pastedText = e.clipboardData?.getData("text") || "";
    const input = e.target as HTMLInputElement;
    const currentValue = input?.value || "";
    const selectionStart = input?.selectionStart ?? currentValue.length;
    const selectionEnd = input?.selectionEnd ?? currentValue.length;

    // Sanitize pasted text: replace invalid chars with nothing (remove them)
    let sanitized = pastedText.replace(/[^a-zA-Z0-9_\-]/g, "");

    // If pasting at the beginning, remove leading - or _
    if (selectionStart === 0) {
      sanitized = sanitized.replace(/^[-_]+/, "");
    }

    // Build new value
    const before = currentValue.slice(0, selectionStart);
    const after = currentValue.slice(selectionEnd);
    const newValue = before + sanitized + after;

    // Update the input value
    input.value = newValue;
    this.newVarName = newValue;

    // Set cursor position after pasted content
    const newCursorPos = selectionStart + sanitized.length;
    requestAnimationFrame(() => {
      input.setSelectionRange(newCursorPos, newCursorPos);
    });

    // Dispatch input event so the form field updates, but DON'T bubble to prevent triggering main input handler
    input.dispatchEvent(new Event("input", { bubbles: false }));
  }

  /**
   * Handle input in Variable Name field - sanitize any invalid input
   * This catches cases where the browser autocomplete or other mechanisms insert invalid chars
   */
  private handleVarNameInput(e: Event) {
    // Prevent event from bubbling up to main input handlers
    e.stopPropagation();
    
    const input = e.target as HTMLInputElement;
    let value = input?.value || "";

    // Remove invalid characters
    const sanitized = value.replace(/[^a-zA-Z0-9_\-]/g, "").replace(/^[\-_]+/, "");

    if (sanitized !== value) {
      const cursorPos = input.selectionStart ?? sanitized.length;
      input.value = sanitized;
      this.newVarName = sanitized;
      // Try to maintain cursor position
      const newPos = Math.min(cursorPos, sanitized.length);
      requestAnimationFrame(() => {
        input.setSelectionRange(newPos, newPos);
      });
    } else {
      this.newVarName = value;
    }
  }

  /** Handle submitting the add variable form */
  private handleAddVariableSubmit(e?: Event) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Read values directly from form fields
    const nameField = this.shadowRoot?.querySelector('jha-form-input[data-field="varName"]') as any;
    const descField = this.shadowRoot?.querySelector('jha-form-input[data-field="varDescription"]') as any;
    const regexField = this.shadowRoot?.querySelector('jha-form-input[data-field="varRegex"]') as any;
    const regexDescField = this.shadowRoot?.querySelector('jha-form-input[data-field="varRegexDesc"]') as any;

    const varName = (nameField?.value || this.newVarName || "").trim();
    const varDesc = (descField?.value || this.newVarDescription || "").trim();
    const varRegex = (regexField?.value || this.newVarRegex || "").trim();
    const varRegexDesc = (regexDescField?.value || this.newVarRegexDescription || "").trim();

    // Validate required fields
    let hasErrors = false;

    if (!varName) {
      if (nameField) {
        nameField.invalid = true;
        nameField.warning = "Variable Name is required";
      }
      hasErrors = true;
    }

    if (!varDesc) {
      if (descField) {
        descField.invalid = true;
        descField.warning = "Description is required";
      }
      hasErrors = true;
    }

    // Validate variable name format - must start with letter or number
    const validNameRegex = /^[a-zA-Z0-9][a-zA-Z0-9_\-]*$/;
    if (varName && !validNameRegex.test(varName)) {
      if (nameField) {
        nameField.invalid = true;
        nameField.warning = "Must start with letter or number. Only letters, numbers, underscores, and dashes allowed.";
      }
      hasErrors = true;
    }

    // Check for duplicate
    const isDuplicate = this.options.some((opt) => opt.name.toLowerCase() === varName.toLowerCase());
    if (isDuplicate) {
      // Show error in the prominent error message area at bottom
      this.formErrorMessage = `Variable already exists`;
      hasErrors = true;
    }

    // Validate regex pattern if provided
    if (varRegex) {
      // Check for common regex mistakes first
      const commonMistake = this.detectCommonRegexMistakes(varRegex);
      if (commonMistake) {
        if (regexField) {
          regexField.invalid = true;
          regexField.warning = commonMistake;
        }
        hasErrors = true;
      } else {
        try {
          new RegExp(varRegex);
        } catch (e) {
          if (regexField) {
            regexField.invalid = true;
            regexField.warning = "Invalid regex pattern syntax";
          }
          hasErrors = true;
        }
      }
    }

    if (hasErrors) return;

    // Create new variable data (camelCase to match WorkflowTemplateVariable interface)
    const newVariable: NewVariableData = {
      name: varName,
      description: varDesc,
      regexRule: varRegex,
      ruleDescription: varRegexDesc,
    };

    // Dispatch event for parent to handle
    this.dispatchEvent(
      new CustomEvent("variable-added", {
        detail: newVariable,
        bubbles: true,
        composed: true,
      })
    );

    // Insert the new variable into the input immediately
    // This provides immediate feedback and prevents "undefined" issues
    const variableText = `{${newVariable.name}}`;
    const currentValue = this.value || "";
    let newCursorPos: number;

    if (this.editingVariableRange) {
      // Replace existing variable
      const before = currentValue.slice(0, this.editingVariableRange[0]);
      const after = currentValue.slice(this.editingVariableRange[1]);
      this.value = before + variableText + after;
      newCursorPos = before.length + variableText.length;
    } else {
      // Insert at cursor position
      const insertPos = this.insertPosition ?? currentValue.length;
      const before = currentValue.slice(0, insertPos);
      const after = currentValue.slice(insertPos);
      this.value = before + variableText + after;
      newCursorPos = before.length + variableText.length;
    }

    this.previousValue = this.value;

    // Close modal and dispatch change
    this.closeDropdown();
    this.requestUpdate();
    this.dispatchChangeEvent();

    // Focus back on input and position cursor
    setTimeout(() => {
      const nativeInput = this.inputEl;
      if (nativeInput) {
        nativeInput.focus();
        nativeInput.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 100);
  }

  // ============================================
  // Helper Methods
  // ============================================

  /**
   * Handle mouse enter on menu item - show tooltip
   */
  private handleMenuItemHover(e: MouseEvent, description: string | undefined, itemIndex: number) {
    const target = e.currentTarget as HTMLElement;
    if (!target) return;

    // Determine tooltip text
    let tooltipText: string;
    if (itemIndex === -1) {
      // Add New Variable item
      tooltipText = "Click to add a new variable to this template's variable list";
    } else if (description) {
      tooltipText = description;
    } else {
      tooltipText = "No description available";
    }

    // Calculate tooltip position with smart positioning to avoid going off-screen
    this.calculateTooltipPosition(target, tooltipText);
    this.activeTooltip = { text: tooltipText, itemIndex };
  }

  /**
   * Handle mouse leave on menu item - hide tooltip
   */
  private handleMenuItemLeave() {
    this.activeTooltip = null;
  }

  /**
   * Calculate tooltip position to ensure it never goes off-screen
   */
  private calculateTooltipPosition(itemElement: HTMLElement, tooltipText: string) {
    const itemRect = itemElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Estimate tooltip dimensions (approximate based on text length)
    const tooltipMaxWidth = 280;
    const estimatedLines = Math.ceil((tooltipText.length * 7) / tooltipMaxWidth); // ~7px per char
    const tooltipHeight = Math.max(32, estimatedLines * 20 + 16); // line height + padding
    const tooltipWidth = Math.min(tooltipMaxWidth, tooltipText.length * 7);
    
    const gap = 8; // Gap between item and tooltip
    const padding = 10; // Edge padding from viewport

    // Try positions in order of preference: right, left, bottom, top
    const positions = [
      {
        side: "right" as const,
        left: itemRect.right + gap,
        top: itemRect.top + itemRect.height / 2 - tooltipHeight / 2,
        fitsHorizontal: itemRect.right + gap + tooltipWidth + padding < viewportWidth,
        fitsVertical: itemRect.top + itemRect.height / 2 - tooltipHeight / 2 >= padding &&
                      itemRect.top + itemRect.height / 2 + tooltipHeight / 2 + padding <= viewportHeight
      },
      {
        side: "left" as const,
        left: itemRect.left - gap - tooltipWidth,
        top: itemRect.top + itemRect.height / 2 - tooltipHeight / 2,
        fitsHorizontal: itemRect.left - gap - tooltipWidth >= padding,
        fitsVertical: itemRect.top + itemRect.height / 2 - tooltipHeight / 2 >= padding &&
                      itemRect.top + itemRect.height / 2 + tooltipHeight / 2 + padding <= viewportHeight
      },
      {
        side: "bottom" as const,
        left: itemRect.left + itemRect.width / 2 - tooltipWidth / 2,
        top: itemRect.bottom + gap,
        fitsHorizontal: itemRect.left + itemRect.width / 2 - tooltipWidth / 2 >= padding &&
                       itemRect.left + itemRect.width / 2 + tooltipWidth / 2 + padding <= viewportWidth,
        fitsVertical: itemRect.bottom + gap + tooltipHeight + padding <= viewportHeight
      },
      {
        side: "top" as const,
        left: itemRect.left + itemRect.width / 2 - tooltipWidth / 2,
        top: itemRect.top - gap - tooltipHeight,
        fitsHorizontal: itemRect.left + itemRect.width / 2 - tooltipWidth / 2 >= padding &&
                       itemRect.left + itemRect.width / 2 + tooltipWidth / 2 + padding <= viewportWidth,
        fitsVertical: itemRect.top - gap - tooltipHeight >= padding
      }
    ];

    // Find first position that fits
    let bestPosition = positions[0]; // Default to right
    for (const pos of positions) {
      if (pos.fitsHorizontal && pos.fitsVertical) {
        bestPosition = pos;
        break;
      }
    }

    // Clamp to viewport boundaries as last resort
    let finalLeft = bestPosition.left;
    let finalTop = bestPosition.top;

    // Ensure tooltip doesn't go off-screen horizontally
    if (finalLeft < padding) {
      finalLeft = padding;
    } else if (finalLeft + tooltipWidth > viewportWidth - padding) {
      finalLeft = viewportWidth - tooltipWidth - padding;
    }

    // Ensure tooltip doesn't go off-screen vertically
    if (finalTop < padding) {
      finalTop = padding;
    } else if (finalTop + tooltipHeight > viewportHeight - padding) {
      finalTop = viewportHeight - tooltipHeight - padding;
    }

    this.tooltipPosition = {
      top: finalTop,
      left: finalLeft,
      preferredSide: bestPosition.side
    };
  }

  /** Open the dropdown */
  private openDropdown() {
    this.originalValue = this.value || "";
    this.isDropdownOpen = true;
    this.editingVariableRange = null;
    
    // Simple positioning: always show below the input
    // Uses absolute positioning relative to input-wrapper
    requestAnimationFrame(() => {
      const inputWrapper = this.shadowRoot?.querySelector(".input-wrapper") as HTMLElement;
      if (inputWrapper) {
        const rect = inputWrapper.getBoundingClientRect();
        const inputHeight = inputWrapper.offsetHeight;
        const gap = 2;
        
        this.dropdownPosition = {
          top: inputHeight + gap,
          left: 0,
          width: rect.width,
          maxHeight: 200,
          flipAbove: false,
        };
        
        // Scroll the dropdown into view after it renders
        requestAnimationFrame(() => {
          const dropdown = this.shadowRoot?.querySelector(".dropdown-container") as HTMLElement;
          if (dropdown) {
            dropdown.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }
        });
      }
    });
  }

  /**
   * Detect common regex mistakes that are syntactically valid but likely wrong
   * 
   * Catches:
   * 1. Double dots for ranges: [a..z] should be [a-z]
   * 2. Reversed ranges: [z-a] should be [a-z]
   * 3. Missing brackets for character class: a-z should be [a-z]
   * 4. Spaces in character class: [a - z] should be [a-z]
   * 5. Using * alone (matches empty string): * should be .* or .+
   */
  private detectCommonRegexMistakes(pattern: string): string | null {
    // 1. Detect "a..z" or "A..Z" pattern (should be "a-z" or "A-Z")
    if (/[a-zA-Z]\.\.+[a-zA-Z]/.test(pattern)) {
      return "Use hyphen for ranges (e.g., a-z not a..z)";
    }

    // 2. Detect "[a..z]" style patterns inside brackets
    if (/\[[^\]]*\.\.[^\]]*\]/.test(pattern)) {
      return "Use hyphen for ranges (e.g., [a-z] not [a..z])";
    }

    // 3. Detect reversed ranges like [z-a] or [9-0]
    // Look for patterns like "z-a" or "Z-A" or "9-0" inside brackets
    // Use string matching since the regex itself would be invalid
    const reversedRangeMatch = pattern.match(/\[([^\]]*)\]/g);
    if (reversedRangeMatch) {
      for (const bracket of reversedRangeMatch) {
        const content = bracket.slice(1, -1); // Remove [ and ]
        // Check for reversed letter ranges (z-a, Z-A, etc.)
        const letterRangeMatch = content.match(/([a-zA-Z])-([a-zA-Z])/g);
        if (letterRangeMatch) {
          for (const range of letterRangeMatch) {
            const start = range.charCodeAt(0);
            const end = range.charCodeAt(2);
            if (start > end) {
              return "Range is reversed (e.g., use [a-z] not [z-a])";
            }
          }
        }
        // Check for reversed number ranges (9-0, etc.)
        const numberRangeMatch = content.match(/([0-9])-([0-9])/g);
        if (numberRangeMatch) {
          for (const range of numberRangeMatch) {
            const start = parseInt(range[0]);
            const end = parseInt(range[2]);
            if (start > end) {
              return "Range is reversed (e.g., use [0-9] not [9-0])";
            }
          }
        }
      }
    }

    // 4. Detect spaces around hyphens in character class: [a - z]
    if (/\[[^\]]*\s+-\s*[^\]]*\]|\[[^\]]*\s*-\s+[^\]]*\]/.test(pattern)) {
      return "Remove spaces around hyphen in range (e.g., [a-z] not [a - z])";
    }

    // 5. Detect standalone * without preceding character (common mistake)
    if (/^\*$/.test(pattern)) {
      return "Use .* for 'anything' or .+ for 'at least one character'";
    }

    return null;
  }

  /** Close the dropdown */
  private closeDropdown() {
    // Check if modal was open before closing (for event dispatch)
    const wasModalOpen = this.showAddVariableForm;
    
    this.isDropdownOpen = false;
    this.showAddVariableForm = false;
    this.editingVariableRange = null;
    this.activeTooltip = null; // Clear tooltip when closing dropdown
    this.resetAddVariableForm();
    
    // If modal was open, dispatch close event and remove class
    if (wasModalOpen) {
      this.classList.remove("modal-active");
      this.dispatchEvent(
        new CustomEvent("modal-closed", {
          bubbles: true,
          composed: true,
        })
      );
    }
  }

  /** Cancel changes and close dropdown */
  private cancelAndClose() {
    this.value = this.originalValue;
    this.previousValue = this.originalValue;
    this.resetAddVariableForm();
    this.editingVariableRange = null;
    this.closeDropdown(); // This now handles modal cleanup
    this.dispatchChangeEvent();

    setTimeout(() => {
      this.inputEl?.focus();
    }, 100);
  }

  /** Reset the add variable form fields */
  private resetAddVariableForm() {
    this.newVarName = "";
    this.newVarDescription = "";
    this.newVarRegex = "";
    this.newVarRegexDescription = "";
    this.formErrorMessage = "";
    
    // Clear any field-level errors
    setTimeout(() => {
      const nameField = this.shadowRoot?.querySelector('jha-form-input[data-field="varName"]') as any;
      const descField = this.shadowRoot?.querySelector('jha-form-input[data-field="varDescription"]') as any;
      if (nameField) {
        nameField.invalid = false;
        nameField.warning = "";
      }
      if (descField) {
        descField.invalid = false;
        descField.warning = "";
      }
    }, 0);
  }

  /** Dispatch change event */
  private dispatchChangeEvent() {
    this.dispatchEvent(
      new CustomEvent("change", {
        detail: { value: this.value },
        bubbles: true,
        composed: true,
      })
    );
  }

  /** Detect if a variable was partially deleted */
  private detectPartialVariableDeletion(oldValue: string, newValue: string): boolean {
    const oldVars = this.getVariablesFromValue(oldValue);
    const countTemplatePlaceholders = (s: string) =>
      (s.match(/\{[a-zA-Z0-9][a-zA-Z0-9_\-]*\}/g) || []).length;

    for (const v of oldVars) {
      const fullVar = `{${v.name}}`;
      if (oldValue.includes(fullVar) && !newValue.includes(fullVar)) {
        const partialPatterns = [
          `{${v.name}`,
          `${v.name}}`,
          `{${v.name.slice(0, -1)}`,
          `${v.name.slice(1)}}`,
        ];

        for (const partial of partialPatterns) {
          if (partial.length > 0 && newValue.includes(partial)) {
            return true;
          }
        }

        if (newValue.includes("{") || newValue.includes("}")) {
          const oldPairs = countTemplatePlaceholders(oldValue);
          const newPairs = countTemplatePlaceholders(newValue);
          const oldOpen = (oldValue.match(/\{/g) || []).length;
          const newOpen = (newValue.match(/\{/g) || []).length;

          if (oldPairs > newPairs && oldOpen === newOpen) {
            return true;
          }
        }
      }
    }

    return false;
  }

  /** Find which variable was affected by deletion */
  private findAffectedVariable(
    oldValue: string,
    _newValue: string,
    cursorPos: number
  ): { start: number; end: number; name: string } | null {
    const oldVars = this.getVariablesFromValue(oldValue);

    for (const v of oldVars) {
      if (cursorPos >= v.start && cursorPos <= v.end) {
        return v;
      }
    }

    const currentValue = this.value || "";
    for (const v of oldVars) {
      const fullVar = `{${v.name}}`;
      if (!currentValue.includes(fullVar)) {
        return v;
      }
    }

    return null;
  }

  /** Extract variables from a value string */
  private getVariablesFromValue(val: string): Array<{ start: number; end: number; name: string }> {
    const variables: Array<{ start: number; end: number; name: string }> = [];
    const regex = /\{([a-zA-Z0-9][a-zA-Z0-9_\-]*)\}/g;
    let match;

    while ((match = regex.exec(val)) !== null) {
      variables.push({
        start: match.index,
        end: match.index + match[0].length,
        name: match[1],
      });
    }

    return variables;
  }

  // ============================================
  // Render Methods
  // ============================================

  /** Render the dropdown menu */
  private renderDropdown() {
    if (!this.isDropdownOpen) return nothing;

    if (this.showAddVariableForm) {
      return html`
        <div class="modal-overlay" @click=${this.handleOverlayClick}>
          <div class="modal-container" @click=${(e: Event) => e.stopPropagation()}>${this.renderAddVariableForm()}</div>
        </div>
      `;
    }

    // Use fixed positioning with calculated coordinates to escape overflow:hidden containers
    // Don't set width - let CSS min-width (220px) and max-width (320px) control the dropdown size
    // Apply dynamic maxHeight to ensure dropdown fits in available space
    const dropdownStyle = `top: ${this.dropdownPosition.top}px; left: ${this.dropdownPosition.left}px; --dropdown-max-height: ${this.dropdownPosition.maxHeight}px;`;
    return html` <div class="dropdown-container" style=${dropdownStyle} @mousedown=${(e: Event) => e.preventDefault()}>${this.renderVariableList()}</div> `;
  }

  /** Render the variable list */
  private renderVariableList() {
    return html`
      <div class="dropdown-menu" @mousedown=${(e: Event) => e.preventDefault()}>
        <div
          class="menu-item add-variable-item"
          role="button"
          tabindex="0"
          @click=${(e: Event) => this.handleAddVariableClick(e)}
          @mouseenter=${(e: MouseEvent) => this.handleMenuItemHover(e, undefined, -1)}
          @mouseleave=${() => this.handleMenuItemLeave()}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === "Enter") {
              e.preventDefault();
              this.handleAddVariableClick(e);
            }
          }}>
          <jha-icon-plus></jha-icon-plus>
          <span>Add New Variable</span>
        </div>

        ${this.options.length === 0
          ? html`
              <div class="no-variables">
                No variables defined yet.<br />
                Click "Add New Variable" above to create one.
              </div>
            `
          : this.options.map(
              (option, index) => html`
                <div
                  class="menu-item variable-item"
                  role="button"
                  tabindex="0"
                  @click=${() => this.handleVariableSelect(option.name)}
                  @mouseenter=${(e: MouseEvent) => this.handleMenuItemHover(e, option.description, index)}
                  @mouseleave=${() => this.handleMenuItemLeave()}
                  @keydown=${(e: KeyboardEvent) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      this.handleVariableSelect(option.name);
                    }
                  }}>
                  <span class="variable-delimiters">{</span>${option.name}<span class="variable-delimiters">}</span>
                </div>
              `
            )}
      </div>
      ${this.renderTooltip()}
    `;
  }

  /** Render the tooltip for variable hover hints */
  private renderTooltip() {
    if (!this.activeTooltip) return nothing;

    const style = `top: ${this.tooltipPosition.top}px; left: ${this.tooltipPosition.left}px;`;
    const sideClass = `tooltip-${this.tooltipPosition.preferredSide}`;

    return html`
      <div class="variable-tooltip ${sideClass}" style=${style}>
        ${this.activeTooltip.text}
      </div>
    `;
  }

  /** Render the add variable form */
  private renderAddVariableForm() {
    return html`
      <div class="add-variable-form" @mousedown=${(e: Event) => e.stopPropagation()}>
        <div class="form-title">
          <span>Add New Variable</span>
          <button class="form-close" type="button" @click=${(e: Event) => this.handleAddVariableCancel(e)} aria-label="Close">
            ×
          </button>
        </div>

        <div class="form-grid">
          <div class="form-field">
            <jha-form-input
              outline
              required
              label="Variable Name"
              placeholder="e.g., businessUnit"
              pattern="^[a-zA-Z0-9][a-zA-Z0-9_\\-]*$"
              warning="Must start with letter or number. Only letters, numbers, underscores, and dashes allowed."
              .value=${this.newVarName}
              @keydown=${this.handleVarNameKeydown}
              @paste=${this.handleVarNamePaste}
              @input=${this.handleVarNameInput}
              @change=${(e: Event) => this.handleFormFieldChange("name", e)}
              data-field="varName"></jha-form-input>
            <div class="form-hint">Must start with letter or number. Alphanumeric, underscores, dashes only.</div>
          </div>

          <div class="form-field">
            <jha-form-input
              outline
              required
              label="Description"
              placeholder="Describe what this variable represents"
              .value=${this.newVarDescription}
              @change=${(e: Event) => this.handleFormFieldChange("description", e)}
              data-field="varDescription"></jha-form-input>
          </div>

          <div class="form-field optional">
            <jha-form-input
              outline
              label="Validation Rule (Regex)"
              placeholder="e.g., ^[0-9]{9}$"
              .value=${this.newVarRegex}
              @change=${(e: Event) => this.handleFormFieldChange("regex", e)}
              data-field="varRegex"></jha-form-input>
            <div class="form-hint">Regex pattern to validate values.</div>
          </div>

          <div class="form-field optional">
            <jha-form-input
              outline
              label="Rule Description"
              placeholder="e.g., Must be 9 digits"
              .value=${this.newVarRegexDescription}
              @change=${(e: Event) => this.handleFormFieldChange("regexDesc", e)}
              data-field="varRegexDesc"></jha-form-input>
            <div class="form-hint">Error message when validation fails.</div>
          </div>

          ${this.formErrorMessage
            ? html`
                <div class="form-error-message-bottom">
                  ${this.formErrorMessage}
                </div>
              `
            : nothing}

          <div class="form-actions">
            <jha-button outline sync type="button" @click=${(e: Event) => this.handleAddVariableCancel(e)}> Cancel </jha-button>
            <jha-button sync type="button" @click=${(e: Event) => this.handleAddVariableSubmit(e)}> Add Variable </jha-button>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    // Show warning if: (required, touched, and empty) OR forceShowWarning is true
    const shouldShowWarning = this.forceShowWarning || (this.required && this.touched && !(this.value || "").trim());

    return html`
      <div class="input-container">
        <!-- Custom label with colored asterisk for required fields -->
        ${this.label
          ? html`<label class="field-label"
              >${this.label}${this.required ? html`<span class="required-asterisk"> *</span>` : nothing}</label
            >`
          : nothing}

        <div class="input-wrapper ${shouldShowWarning ? "has-error" : ""}">
          <jh-input
            id="template-input"
            type="text"
            autocomplete="off"
            outline
            placeholder=${this.placeholder}
            ?disabled=${this.disabled}
            ?invalid=${shouldShowWarning}
            maxlength=${this.maxlength > 0 ? String(this.maxlength) : nothing}
            .maxlength=${this.maxlength > 0 ? String(this.maxlength) : null}
            .value=${this.value}
            @input=${this.handleInput}
            @keydown=${this.handleKeydown}
            @keyup=${this.saveCursorPosition}
            @click=${this.handleInputClick}
            @mouseup=${this.saveCursorPosition}
            @focus=${this.saveCursorPosition}
            @blur=${this.handleBlur}></jh-input>

          <jha-tooltip 
            ?top=${this.variableButtonTooltipPosition === "top"}
            ?top-left=${this.variableButtonTooltipPosition === "top-left"}
            ?top-right=${this.variableButtonTooltipPosition === "top-right"}
            ?bottom=${this.variableButtonTooltipPosition === "bottom"}
            ?bottom-left=${this.variableButtonTooltipPosition === "bottom-left"}
            ?bottom-right=${this.variableButtonTooltipPosition === "bottom-right"}
            ?left=${this.variableButtonTooltipPosition === "left"}
            ?right=${this.variableButtonTooltipPosition === "right"}
            text="Click to insert a variable">
            <button
              class="variable-button"
              type="button"
              ?disabled=${this.disabled}
              @click=${this.handleVariableButtonClick}
              aria-label="Insert variable">
              <jh-icon-arrow-down-to-bracket></jh-icon-arrow-down-to-bracket>
            </button>
          </jha-tooltip>
          ${this.renderDropdown()}
        </div>
        <!-- Validation error message -->
        ${shouldShowWarning ? html`<span class="validation-message">${this.errorText || `${this.label} is required`}</span>` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "template-string-input": TemplateStringInput;
  }
}
